import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { DatePickerDE } from '../components/ui/date-picker-de';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../components/ui/command';
import { jobsAPI, locationsAPI, phonebookAPI, careersAPI, recruitingAPI } from '../lib/api';
import { usePendingTasks } from '../contexts/PendingTasksContext';
import {
  Briefcase, Plus, MapPin, Clock, Calendar, User, FileText,
  MoreVertical, Pencil, Trash2, Pin, CheckCircle2, XCircle,
  Search, Filter, Building2, Loader2, Users, Phone, Mail, Check, ChevronsUpDown, ExternalLink,
  Share2, Copy, Download, QrCode,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const STATUS_CONFIG = {
  open: { label: 'Offen', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300', icon: Briefcase },
  filled: { label: 'Besetzt', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', icon: CheckCircle2 },
  closed: { label: 'Geschlossen', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300', icon: XCircle },
};

const EMPLOYMENT_TYPES = ['Vollzeit', 'Teilzeit', 'Minijob', 'Praktikum', 'Ausbildung', 'Werkstudent'];
const CONTRACT_TYPES = ['Unbefristet', 'Befristet'];

const emptyForm = {
  title: '', description: '', location_id: '', department: '',
  employment_type: 'Vollzeit', contract_type: 'Unbefristet',
  weekly_hours: '', salary_range: '', requirements: '', benefits: '',
  contact_person: '', contact_email: '', contact_phone: '', start_date: '', deadline: '',
  is_internal: true, is_pinned: false, is_public: false,
};

export default function JobPostings() {
  const { isAdmin, hasPermission } = useAuth();
  const navigate = useNavigate();
  const { reload: reloadPendingTasks } = usePendingTasks();
  const canManage = isAdmin || hasPermission('job_postings', 'edit');
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [phoneContacts, setPhoneContacts] = useState([]);
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('open');
  const [filterSearch, setFilterSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [shareJob, setShareJob] = useState(null);
  const [posterLoading, setPosterLoading] = useState(false);
  const [detailJob, setDetailJob] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [jobsRes, locRes] = await Promise.all([
        jobsAPI.getAll(),
        locationsAPI.getAll(),
      ]);
      setJobs(jobsRes.data || []);
      setLocations(locRes.data || []);
    } catch (error) {
      console.error('Failed to load jobs:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  // Telefonbuch-Kontakte fuer Ansprechpartner-Vorschlag laden (nur Verwalter brauchen sie)
  const loadPhoneContacts = useCallback(async () => {
    if (!canManage || phoneContacts.length) return;
    try {
      const res = await phonebookAPI.getAll();
      const flat = [];
      (res.data?.branches || []).forEach((branch) => {
        (branch.locations || []).forEach((loc) => {
          (loc.employees || []).forEach((emp) => {
            const displayName = `${emp.first_name} ${emp.last_name}`.trim() || emp.name;
            flat.push({
              id: emp.id,
              name: displayName,
              phone: emp.mobile || emp.phone || '',
              email: emp.email || '',
              position: emp.position || '',
              location: loc.name || '',
            });
          });
        });
      });
      flat.sort((a, b) => a.name.localeCompare(b.name, 'de'));
      setPhoneContacts(flat);
    } catch (error) {
      console.error('Failed to load phonebook contacts:', error);
    }
  }, [canManage, phoneContacts.length]);

  useEffect(() => { loadData(); }, [loadData]);

  // Beim Öffnen der Seite als "gesehen" markieren -> grünes Sidebar-Badge verschwindet
  useEffect(() => {
    jobsAPI.markSeen()
      .then(() => reloadPendingTasks())
      .catch(() => {});
  }, [reloadPendingTasks]);

  const filteredJobs = jobs.filter(j => {
    if (filterStatus !== 'all' && j.status !== filterStatus) return false;
    if (filterSearch) {
      const s = filterSearch.toLowerCase();
      return j.title.toLowerCase().includes(s) || (j.department || '').toLowerCase().includes(s) || (j.location_name || '').toLowerCase().includes(s);
    }
    return true;
  });

  const openDialog = (job = null) => {
    loadPhoneContacts();
    if (job) {
      setEditingJob(job);
      setForm({
        title: job.title, description: job.description,
        location_id: job.location_id || '', department: job.department || '',
        employment_type: job.employment_type, contract_type: job.contract_type,
        weekly_hours: job.weekly_hours || '', salary_range: job.salary_range || '',
        requirements: job.requirements || '', benefits: job.benefits || '',
        contact_person: job.contact_person || '', contact_email: job.contact_email || '',
        contact_phone: job.contact_phone || '',
        start_date: job.start_date || '', deadline: job.deadline || '',
        is_internal: job.is_internal, is_pinned: job.is_pinned, is_public: job.is_public ?? false,
      });
    } else {
      setEditingJob(null);
      setForm({ ...emptyForm });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.description) {
      toast.error('Bitte Titel und Beschreibung ausfuellen');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        location_id: form.location_id || null,
        weekly_hours: form.weekly_hours ? parseFloat(form.weekly_hours) : null,
        salary_range: form.salary_range || null,
        requirements: form.requirements || null,
        benefits: form.benefits || null,
        contact_person: form.contact_person || null,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
        start_date: form.start_date || null,
        deadline: form.deadline || null,
      };
      if (editingJob) {
        await jobsAPI.update(editingJob.id, payload);
        toast.success('Stellenausschreibung aktualisiert');
      } else {
        await jobsAPI.create(payload);
        toast.success('Stellenausschreibung erstellt');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await jobsAPI.updateStatus(id, status);
      toast.success('Status aktualisiert');
      loadData();
    } catch {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Stellenausschreibung wirklich loeschen?')) return;
    try {
      await jobsAPI.delete(id);
      toast.success('Stellenausschreibung geloescht');
      loadData();
    } catch {
      toast.error('Fehler beim Loeschen');
    }
  };

  const formatDate = (d) => {
    try { return format(parseISO(d), 'dd.MM.yyyy', { locale: de }); } catch { return d; }
  };

  const shareUrl = shareJob ? `${window.location.origin}/karriere/${shareJob.id}` : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link kopiert');
    } catch { toast.error('Kopieren fehlgeschlagen'); }
  };

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadQr = async () => {
    try {
      const res = await fetch(careersAPI.qrUrl(shareJob.id, window.location.origin));
      triggerDownload(await res.blob(), `QR_${shareJob.title || 'stelle'}.png`);
    } catch { toast.error('QR-Download fehlgeschlagen'); }
  };

  const downloadPoster = async () => {
    setPosterLoading(true);
    try {
      const { data } = await recruitingAPI.getPoster(shareJob.id, window.location.origin);
      triggerDownload(data, `Aushang_${shareJob.title || 'stelle'}.pdf`);
    } catch { toast.error('PDF-Aushang fehlgeschlagen'); }
    finally { setPosterLoading(false); }
  };

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="space-y-4">{[1, 2, 3].map(i => <Skeleton key={`s-${i}`} className="h-40 rounded-xl" />)}</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="job-postings-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading font-bold text-xl sm:text-3xl text-foreground">Stellenausschreibungen</h1>
            <p className="text-muted-foreground mt-1">
              {filteredJobs.length} {filterStatus === 'all' ? 'gesamt' : filterStatus === 'open' ? 'offene Stellen' : 'Eintraege'}
            </p>
          </div>
          {canManage && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => navigate('/recruiting')} data-testid="goto-recruiting-btn">
                <Users className="mr-2 h-4 w-4" /> Bewerber
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => window.open('/karriere', '_blank')} data-testid="open-careers-btn">
                <ExternalLink className="mr-2 h-4 w-4" /> Karriereseite
              </Button>
              <Button className="rounded-full" onClick={() => openDialog()} data-testid="new-job-btn">
                <Plus className="mr-2 h-4 w-4" /> Neue Stelle
              </Button>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Suche nach Titel, Abteilung, Standort..."
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              className="pl-9"
              data-testid="job-search"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {['all', 'open', 'filled', 'closed'].map(s => (
              <Button
                key={s}
                variant={filterStatus === s ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus(s)}
                data-testid={`filter-${s}`}
                className="flex-1 sm:flex-none"
              >
                {s === 'all' ? 'Alle' : STATUS_CONFIG[s].label}
              </Button>
            ))}
          </div>
        </div>

        {/* Job List */}
        {filteredJobs.length > 0 ? (
          <div className="space-y-4">
            {filteredJobs.map((job) => {
              const statusCfg = STATUS_CONFIG[job.status] || STATUS_CONFIG.open;
              const StatusIcon = statusCfg.icon;

              return (
                <Card key={job.id} className="border-border/60 hover:shadow-md transition-shadow" data-testid={`job-card-${job.id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div
                        className="space-y-3 flex-1 min-w-0 cursor-pointer"
                        onClick={() => setDetailJob(job)}
                        data-testid={`job-open-detail-${job.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="p-2.5 rounded-lg bg-primary/10 flex-shrink-0">
                            <Briefcase className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-heading font-semibold text-lg">{job.title}</h3>
                              {job.is_pinned && <Pin className="h-4 w-4 text-amber-500 flex-shrink-0" />}
                              <Badge className={`text-xs ${statusCfg.color}`}>
                                <StatusIcon className="h-3 w-3 mr-1" />
                                {statusCfg.label}
                              </Badge>
                            </div>
                            <div className="flex flex-wrap gap-3 mt-1.5 text-sm text-muted-foreground">
                              {job.location_name && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3.5 w-3.5" /> {job.location_name}
                                </span>
                              )}
                              {job.department && (
                                <span className="flex items-center gap-1">
                                  <Building2 className="h-3.5 w-3.5" /> {job.department}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" /> {job.employment_type}
                              </span>
                              {job.weekly_hours && (
                                <span>{job.weekly_hours}h/Woche</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <p className="text-sm text-muted-foreground line-clamp-3 whitespace-pre-line">
                          {job.description}
                        </p>

                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline" className="text-xs">{job.contract_type}</Badge>
                          {job.start_date && (
                            <Badge variant="outline" className="text-xs">
                              <Calendar className="h-3 w-3 mr-1" /> Ab {formatDate(job.start_date)}
                            </Badge>
                          )}
                          {job.deadline && (
                            <Badge variant="outline" className="text-xs text-amber-700 border-amber-300">
                              Bewerbungsfrist: {formatDate(job.deadline)}
                            </Badge>
                          )}
                          {job.is_internal && (
                            <Badge variant="secondary" className="text-xs">
                              <Users className="h-3 w-3 mr-1" /> Intern
                            </Badge>
                          )}
                        </div>

                        {(job.contact_person || job.contact_email || job.contact_phone) && (
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            {job.contact_person && (
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" /> Ansprechpartner: {job.contact_person}
                              </span>
                            )}
                            {job.contact_phone && (
                              <a href={`tel:${job.contact_phone}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 hover:text-primary" data-testid={`job-contact-phone-${job.id}`}>
                                <Phone className="h-3 w-3" /> {job.contact_phone}
                              </a>
                            )}
                            {job.contact_email && (
                              <a href={`mailto:${job.contact_email}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 hover:text-primary" data-testid={`job-contact-email-${job.id}`}>
                                <Mail className="h-3 w-3" /> {job.contact_email}
                              </a>
                            )}
                          </div>
                        )}
                      </div>

                      {canManage && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" data-testid={`job-menu-${job.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openDialog(job)}>
                              <Pencil className="mr-2 h-4 w-4" /> Bearbeiten
                            </DropdownMenuItem>
                            {job.is_public && (
                              <DropdownMenuItem onClick={() => setShareJob(job)} data-testid={`job-share-${job.id}`}>
                                <Share2 className="mr-2 h-4 w-4" /> Teilen & Aushang
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            {job.status !== 'open' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(job.id, 'open')}>
                                <Briefcase className="mr-2 h-4 w-4" /> Als offen markieren
                              </DropdownMenuItem>
                            )}
                            {job.status !== 'filled' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(job.id, 'filled')}>
                                <CheckCircle2 className="mr-2 h-4 w-4" /> Als besetzt markieren
                              </DropdownMenuItem>
                            )}
                            {job.status !== 'closed' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(job.id, 'closed')}>
                                <XCircle className="mr-2 h-4 w-4" /> Schliessen
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDelete(job.id)} className="text-destructive">
                              <Trash2 className="mr-2 h-4 w-4" /> Loeschen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="border-border/60">
            <CardContent className="py-16">
              <div className="text-center">
                <Briefcase className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <h3 className="font-heading font-semibold text-lg">Keine Stellenausschreibungen</h3>
                <p className="text-muted-foreground mt-1">
                  {filterStatus !== 'all' || filterSearch
                    ? 'Keine Ergebnisse fuer die gewaehlten Filter.'
                    : 'Noch keine Stellen ausgeschrieben.'}
                </p>
                {canManage && !filterSearch && filterStatus === 'open' && (
                  <Button className="mt-4 rounded-full" onClick={() => openDialog()}>
                    <Plus className="mr-2 h-4 w-4" /> Erste Stelle ausschreiben
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingJob ? 'Stellenausschreibung bearbeiten' : 'Neue Stellenausschreibung'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              {/* Title */}
              <div className="space-y-2">
                <Label>Titel *</Label>
                <Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="z.B. Paedagogische Fachkraft (m/w/d)" data-testid="job-title-input" />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>Beschreibung *</Label>
                <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Aufgaben, Anforderungen, Wir bieten..." rows={6} data-testid="job-description-input" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Location */}
                <div className="space-y-2">
                  <Label>Standort</Label>
                  <Select value={form.location_id || 'none'} onValueChange={(v) => set('location_id', v === 'none' ? '' : v)}>
                    <SelectTrigger data-testid="job-location"><SelectValue placeholder="Standort waehlen" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Kein Standort</SelectItem>
                      {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Department */}
                <div className="space-y-2">
                  <Label>Massnahme/Abteilung</Label>
                  <Input value={form.department} onChange={(e) => set('department', e.target.value)} placeholder="z.B. BvB, Reha" data-testid="job-department" />
                </div>

                {/* Employment Type */}
                <div className="space-y-2">
                  <Label>Beschaeftigungsart</Label>
                  <Select value={form.employment_type} onValueChange={(v) => set('employment_type', v)}>
                    <SelectTrigger data-testid="job-employment-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EMPLOYMENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Contract Type */}
                <div className="space-y-2">
                  <Label>Vertragsart</Label>
                  <Select value={form.contract_type} onValueChange={(v) => set('contract_type', v)}>
                    <SelectTrigger data-testid="job-contract-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CONTRACT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Weekly Hours */}
                <div className="space-y-2">
                  <Label>Wochenstunden</Label>
                  <Input type="number" value={form.weekly_hours} onChange={(e) => set('weekly_hours', e.target.value)} placeholder="z.B. 39" data-testid="job-hours" />
                </div>

                {/* Start Date */}
                <div className="space-y-2">
                  <Label>Startdatum</Label>
                  <DatePickerDE value={form.start_date} onChange={(v) => set('start_date', v)} placeholder="Startdatum" testId="job-start-date" />
                </div>

                {/* Deadline */}
                <div className="space-y-2">
                  <Label>Bewerbungsfrist</Label>
                  <DatePickerDE value={form.deadline} onChange={(v) => set('deadline', v)} placeholder="Bewerbungsfrist" testId="job-deadline" />
                </div>
              </div>

              {/* Ansprechpartner + Kontaktdaten */}
              <div className="space-y-3 p-4 rounded-lg border border-border/60 bg-secondary/20">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <Label className="text-sm font-semibold">Ansprechpartner &amp; Kontakt</Label>
                  <Popover open={contactPickerOpen} onOpenChange={setContactPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        role="combobox"
                        className="justify-between sm:w-64 rounded-full"
                        data-testid="job-contact-picker-trigger"
                      >
                        <span className="flex items-center gap-2 truncate">
                          <Users className="h-4 w-4" /> Aus Telefonbuch übernehmen
                        </span>
                        <ChevronsUpDown className="h-4 w-4 opacity-50 flex-shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[min(24rem,90vw)] p-0" align="end">
                      <Command>
                        <CommandInput placeholder="Mitarbeiter suchen..." data-testid="job-contact-picker-search" />
                        <CommandList>
                          <CommandEmpty>Keine Kontakte gefunden.</CommandEmpty>
                          <CommandGroup>
                            {phoneContacts.map((c) => (
                              <CommandItem
                                key={c.id}
                                value={`${c.name} ${c.position} ${c.location}`}
                                onSelect={() => {
                                  setForm(prev => ({
                                    ...prev,
                                    contact_person: c.name,
                                    contact_phone: c.phone,
                                    contact_email: c.email,
                                  }));
                                  setContactPickerOpen(false);
                                }}
                                data-testid={`job-contact-option-${c.id}`}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{c.name}</span>
                                  <span className="text-xs text-muted-foreground">
                                    {[c.position, c.location].filter(Boolean).join(' · ')}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Name</Label>
                    <Input value={form.contact_person} onChange={(e) => set('contact_person', e.target.value)} placeholder="Name" data-testid="job-contact" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Telefon</Label>
                    <Input type="tel" value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} placeholder="z.B. 0301234567" data-testid="job-contact-phone" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Dienstliche E-Mail</Label>
                    <Input type="email" value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} placeholder="name@grone.de" data-testid="job-contact-email" />
                  </div>
                </div>
              </div>

              {/* Requirements */}
              <div className="space-y-2">
                <Label>Anforderungen</Label>
                <Textarea value={form.requirements} onChange={(e) => set('requirements', e.target.value)} placeholder="Qualifikationen, Erfahrung..." rows={3} data-testid="job-requirements" />
              </div>

              {/* Benefits */}
              <div className="space-y-2">
                <Label>Wir bieten</Label>
                <Textarea value={form.benefits} onChange={(e) => set('benefits', e.target.value)} placeholder="Verguetung, Weiterbildung, Benefits..." rows={3} data-testid="job-benefits" />
              </div>

              {/* Pinned */}
              <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg">
                <input type="checkbox" id="job-pinned" checked={form.is_pinned} onChange={(e) => set('is_pinned', e.target.checked)} className="h-4 w-4 rounded" />
                <Label htmlFor="job-pinned" className="font-normal cursor-pointer">Angepinnt (wird oben angezeigt)</Label>
              </div>

              {/* Extern veröffentlichen */}
              <div className="flex items-start gap-3 p-3 bg-primary/5 border border-primary/15 rounded-lg">
                <input type="checkbox" id="job-public" checked={form.is_public} onChange={(e) => set('is_public', e.target.checked)} className="h-4 w-4 rounded mt-0.5" data-testid="job-public" />
                <Label htmlFor="job-public" className="font-normal cursor-pointer leading-snug">
                  Extern veröffentlichen
                  <span className="block text-xs text-muted-foreground">Erscheint auf der öffentlichen Karriereseite – Bewerbung ohne Login möglich.</span>
                </Label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Abbrechen</Button>
                <Button onClick={handleSave} disabled={saving} className="rounded-full" data-testid="save-job-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editingJob ? 'Aktualisieren' : 'Veroeffentlichen'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Detail-Ansicht (read-only) */}
        <Dialog open={!!detailJob} onOpenChange={(o) => !o && setDetailJob(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="job-detail-dialog">
            {detailJob && (() => {
              const statusCfg = STATUS_CONFIG[detailJob.status] || STATUS_CONFIG.open;
              const StatusIcon = statusCfg.icon;
              return (
                <>
                  <DialogHeader>
                    <div className="flex items-start gap-3 pr-6">
                      <div className="p-2.5 rounded-lg bg-primary/10 flex-shrink-0">
                        <Briefcase className="h-5 w-5 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <DialogTitle className="font-heading text-left">{detailJob.title}</DialogTitle>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge className={`text-xs ${statusCfg.color}`}>
                            <StatusIcon className="h-3 w-3 mr-1" /> {statusCfg.label}
                          </Badge>
                          {detailJob.is_pinned && (
                            <Badge variant="outline" className="text-xs"><Pin className="h-3 w-3 mr-1 text-amber-500" /> Angepinnt</Badge>
                          )}
                          {detailJob.is_internal && (
                            <Badge variant="secondary" className="text-xs"><Users className="h-3 w-3 mr-1" /> Intern</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </DialogHeader>

                  <div className="space-y-5 pt-2">
                    {/* Eckdaten */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {detailJob.location_name && (
                        <div><p className="text-xs text-muted-foreground">Standort</p><p className="text-sm font-medium flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {detailJob.location_name}</p></div>
                      )}
                      {detailJob.department && (
                        <div><p className="text-xs text-muted-foreground">Maßnahme/Abteilung</p><p className="text-sm font-medium flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> {detailJob.department}</p></div>
                      )}
                      <div><p className="text-xs text-muted-foreground">Beschäftigungsart</p><p className="text-sm font-medium">{detailJob.employment_type}</p></div>
                      <div><p className="text-xs text-muted-foreground">Vertragsart</p><p className="text-sm font-medium">{detailJob.contract_type}</p></div>
                      {detailJob.weekly_hours && (
                        <div><p className="text-xs text-muted-foreground">Wochenstunden</p><p className="text-sm font-medium">{detailJob.weekly_hours} h</p></div>
                      )}
                      {detailJob.salary_range && (
                        <div><p className="text-xs text-muted-foreground">Vergütung</p><p className="text-sm font-medium">{detailJob.salary_range}</p></div>
                      )}
                      {detailJob.start_date && (
                        <div><p className="text-xs text-muted-foreground">Startdatum</p><p className="text-sm font-medium flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {formatDate(detailJob.start_date)}</p></div>
                      )}
                      {detailJob.deadline && (
                        <div><p className="text-xs text-muted-foreground">Bewerbungsfrist</p><p className="text-sm font-medium text-amber-700">{formatDate(detailJob.deadline)}</p></div>
                      )}
                    </div>

                    {/* Beschreibung */}
                    <div>
                      <p className="text-sm font-semibold mb-1">Beschreibung</p>
                      <p className="text-sm text-muted-foreground whitespace-pre-line">{detailJob.description}</p>
                    </div>

                    {detailJob.requirements && (
                      <div>
                        <p className="text-sm font-semibold mb-1">Anforderungen</p>
                        <p className="text-sm text-muted-foreground whitespace-pre-line">{detailJob.requirements}</p>
                      </div>
                    )}

                    {detailJob.benefits && (
                      <div>
                        <p className="text-sm font-semibold mb-1">Wir bieten</p>
                        <p className="text-sm text-muted-foreground whitespace-pre-line">{detailJob.benefits}</p>
                      </div>
                    )}

                    {/* Kontakt */}
                    {(detailJob.contact_person || detailJob.contact_phone || detailJob.contact_email) && (
                      <div className="p-4 rounded-lg border border-border/60 bg-secondary/20 space-y-2">
                        <p className="text-sm font-semibold">Ansprechpartner &amp; Kontakt</p>
                        {detailJob.contact_person && (
                          <p className="text-sm flex items-center gap-2"><User className="h-4 w-4 text-muted-foreground" /> {detailJob.contact_person}</p>
                        )}
                        {detailJob.contact_phone && (
                          <a href={`tel:${detailJob.contact_phone}`} className="text-sm flex items-center gap-2 hover:text-primary" data-testid="job-detail-phone"><Phone className="h-4 w-4 text-muted-foreground" /> {detailJob.contact_phone}</a>
                        )}
                        {detailJob.contact_email && (
                          <a href={`mailto:${detailJob.contact_email}`} className="text-sm flex items-center gap-2 hover:text-primary" data-testid="job-detail-email"><Mail className="h-4 w-4 text-muted-foreground" /> {detailJob.contact_email}</a>
                        )}
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground">
                      Erstellt von {detailJob.created_by_name} am {formatDate(detailJob.created_at)}
                    </p>

                    {canManage && (
                      <div className="flex justify-end gap-3 pt-1">
                        <Button variant="outline" className="rounded-full" onClick={() => { const j = detailJob; setDetailJob(null); openDialog(j); }} data-testid="job-detail-edit-btn">
                          <Pencil className="mr-2 h-4 w-4" /> Bearbeiten
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Teilen & Aushang Dialog */}
        <Dialog open={!!shareJob} onOpenChange={(o) => !o && setShareJob(null)}>
          <DialogContent data-testid="share-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Share2 className="h-5 w-5 text-primary" /> Stelle teilen</DialogTitle>
            </DialogHeader>
            {shareJob && (
              <div className="space-y-5">
                <p className="text-sm text-muted-foreground">
                  „<strong>{shareJob.title}</strong>" ist öffentlich. Teile den Link oder häng den QR-Aushang aus.
                </p>

                {/* Link */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Öffentlicher Link</Label>
                  <div className="flex gap-2">
                    <Input readOnly value={shareUrl} className="text-xs" data-testid="share-link-input" onFocus={(e) => e.target.select()} />
                    <Button variant="outline" size="icon" onClick={copyLink} data-testid="copy-link-btn" title="Link kopieren">
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => window.open(shareUrl, '_blank')} title="Öffnen">
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* QR */}
                <div className="flex items-center gap-4">
                  <img
                    src={careersAPI.qrUrl(shareJob.id, window.location.origin)}
                    alt="QR-Code" width={128} height={128}
                    className="rounded-lg border bg-white p-1"
                    data-testid="share-qr-img"
                  />
                  <div className="space-y-2">
                    <p className="text-sm font-medium flex items-center gap-1.5"><QrCode className="h-4 w-4" /> QR-Code</p>
                    <p className="text-xs text-muted-foreground">Scannen führt direkt zur Bewerbung.</p>
                    <Button variant="outline" size="sm" onClick={downloadQr} data-testid="download-qr-btn">
                      <Download className="mr-2 h-4 w-4" /> QR-Code speichern
                    </Button>
                  </div>
                </div>

                {/* PDF Aushang */}
                <div className="pt-2 border-t">
                  <Button className="w-full" onClick={downloadPoster} disabled={posterLoading} data-testid="download-poster-btn">
                    {posterLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
                    PDF-Aushang fürs Schwarze Brett
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
